<template>
  <div class="jumbotron">
    <div class="row">
      <h4>Icons</h4>
    </div>
    <div class="row">
      <div class="col-md-4 text-right my-auto">
        <p class="no-margin-bottom">Average Service Cost<span v-if="!getCostLevel" class="red-text">*</span></p>
      </div>
      <div class="col-md-4 my-auto">
        <select class="form-control custom-select" v-model="averageServiceCost" v-bind:class="{'is-invalid': averageCostInvalid}">
          <option>Economical</option>
          <option>Average</option>
          <option>Above Average</option>
          <option>High End</option>
        </select>
        <div class="invalid-feedback">Please select an average cost</div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4 text-right my-auto">
        <p class="no-margin-bottom">Availability<span v-if="!getAvailabilityLevel" class="red-text">*</span></p>
      </div>
      <div class="col-md-4 my-auto">
        <select class="form-control custom-select" v-model="availability" v-bind:class="{'is-invalid': availabilityInvalid}">
          <option>0-2 Weeks</option>
          <option>1-2 Months</option>
          <option>2+ Months</option>
        </select>
        <div class="invalid-feedback">Please select an availability</div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4 text-right my-auto">
        <p class="no-margin-bottom">Emergency Services<span v-if="!getEmergencies" class="red-text">*</span></p>
      </div>
      <div class="col-md-4 my-auto">
        <select class="form-control custom-select" v-model="emergencyServices" v-bind:class="{'is-invalid': emergenciesInvalid}">
          <option>Yes</option>
          <option>No</option>
        </select>
        <div class="invalid-feedback">Please indicate whether you are available for emergencies</div>
      </div>
    </div>
    <div class="row">
      <h4>Service Categories (Up to 3)<span v-if="selectedServices.length === 0" class="red-text">*</span></h4>
    </div>
    <div class="row">
      <div class="col-lg-5 my-auto">
        <select v-model="selectedService" class="form-control" v-bind:class="{'is-invalid': serviceCategoriesInvalid}">
          <option v-for="service in availableServices" v-bind:key="service.id">{{ service }}</option>
        </select>
        <div class="invalid-feedback">You must select service categories</div>
      </div>
      <div class="col-lg-3">
        <button class='btn btn-primary add-button' v-on:click='addService'>Add</button>
      </div>
    </div>
    <div class="row no-margins">
      <b-alert variant="warning"
               dismissible
               :show="showTooManyServicesAlert"
               @dismissed="showTooManyServicesAlert=false">
        You can only add three services at once.
      </b-alert>
    </div>
    <div class="row">
      <div v-for="service in selectedServices" :key="service.id" class="col-md-4">
        <button class="btn btn-secondary remove-button" v-on:click="removeService(service)">{{ service }}<i class="fa fa-window-close" aria-hidden="true"></i></button>
        <div v-if="getHasAdvertisingPlan" class="custom-control custom-checkbox" v-for="specialty in specialties[service]" :key="specialty.id">
          <input class="custom-control-input" type="checkbox" v-bind:id="specialty" v-bind:value="specialty" v-model="selectedSpecialties">
          <label class="custom-control-label" v-bind:for="specialty">{{ specialty }}</label>
        </div>
      </div>
    </div>
    <div class="row">
      <h4>Service Area<span v-if="!(getAreasServiced.length !== 0)" class="red-text">*</span></h4>
      <p class="no-margin-bottom">Click to add points on the map. Connect the points and click Approximate Zip Codes to populate your service area.</p>
    </div>
    <div id="map" class="row map-row">
    </div>
    <div class="row">
      <div class="col-md-4 offset-md-2">
        <button class="btn btn-primary" v-on:click="clearMap">Clear Map</button>
      </div>
      <div class="col-md-4">
        <button class="btn btn-primary" v-on:click="approximateZips">Approximate Zip Codes</button>
      </div>
    </div>
    <form v-on:submit.prevent>
      <div class="row">
        <div class="col-lg-5 my-auto">
          <input v-model="zip" type="text" class="form-control form-control-md" maxlength="5" placeholder="Enter ZIP" v-bind:class="{'is-invalid': serviceAreaInvalid}">
          <div class="invalid-feedback">Please Enter a service area</div>
        </div>
        <div class="col-lg-3 my-auto">
          <button v-on:click="addPlace" class="btn btn-primary add-button">Add Zip</button>
        </div>
      </div>
    </form>
    <b-alert variant="warning"
             dismissible
             :show="showPlaceAlert"
             @dismissed="showPriceAlert=false">
      Must Add A Valid Zip Code.
    </b-alert>
    <div class="row">
      <div class="col">
        <button v-for="(zip, index) in getAreasServiced"
                :key="index"
                v-on:click="removeArea(index)"
                class="btn btn-secondary remove-button">{{ zip }}<i class="fa fa-window-close" aria-hidden="true"></i></button>
      </div>
    </div>
    <div class="row">
      <div class='col-lg-6 offset-lg-3'>
        <button v-if="complete" v-on:click="save" class="btn btn-primary">Save</button>
        <button v-else v-on:click="save" class="btn btn-primary">Save & Continue</button>
      </div>
    </div>
    <div class="row">
      <div class="col-md-10 offset-md-1">
        <b-alert variant="success"
             dismissible
             :show="showDismissibleAlert"
             @dismissed="showDismissibleAlert=false">
          Data Saved
        </b-alert>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import elasticsearch from 'elasticsearch'
import router from '../../../../../router/index.js'
import GooglePlacesApi from '../../../../../lib/googlePlacesApi.js'

const costLevelMapping = {
  'Economical': 100,
  'Average': 200,
  'Above Average': 300,
  'High End': 400
}

const availabilityLevelMapping = {
  '0-2 Weeks': 100,
  '1-2 Months': 200,
  '2+ Months': 300
}

function invert (json) {
  var ret = {}
  for (var key in json) {
    ret[json[key]] = key
  }
  return ret
}

export default {
  data () {
    return {
      polygons: [],
      selectedService: null,
      selectedServices: [],
      availableServices: null,
      zip: null,
      showPlaceAlert: false,
      showTooManyServicesAlert: false,
      averageCostInvalid: false,
      availabilityInvalid: false,
      emergenciesInvalid: false,
      serviceAreaInvalid: false,
      serviceCategoriesInvalid: false,
      formInvalid: false,
      complete: false,
      showDismissibleAlert: false,
      specialties: {}
    }
  },
  computed: {
    ...mapGetters('business', [
      'getSelectedServices',
      'getCostLevel',
      'getAvailabilityLevel',
      'getEmergencies',
      'getAreasServiced',
      'profileIsComplete',
      'getServiceAreaCoords',
      'getSpecialties',
      'getHasAdvertisingPlan'
    ]),
    selectedSpecialties: {
      get () {
        return this.getSpecialties
      },
      set (value) {
        this.updateSpecialties(value)
      }
    },
    averageServiceCost: {
      get () {
        const inverted = invert(costLevelMapping)
        return inverted[this.getCostLevel]
      },
      set (value) {
        this.updateCostLevel(costLevelMapping[value])
      }
    },
    availability: {
      get () {
        const inverted = invert(availabilityLevelMapping)
        return inverted[this.getAvailabilityLevel]
      },
      set (value) {
        this.updateAvailabilityLevel(availabilityLevelMapping[value])
      }
    },
    emergencyServices: {
      get () {
        return this.getEmergencies
      },
      set (value) {
        this.updateEmergencies(value)
      }
    }
  },
  methods: {
    ...mapActions('business', [
      'syncData'
    ]),
    async approximateZips () {
      try {
        if (!this.getServiceAreaCoords.length > 0) return
        var client = new elasticsearch.Client({
          host: window.CONFIG.SearchDomain,
          apiVersion: '6.4'
        })
        const results = await client.search({
          index: 'zip_code_tabulation_areas',
          type: '_doc',
          size: 10000,
          from: 0,
          body: {
            'query': {
              'bool': {
                'must': {
                  'match_all': {}
                },
                'filter': {
                  'geo_shape': {
                    'geometry': {
                      'relation': 'intersects',
                      'shape': {
                        'coordinates': [this.getServiceAreaCoords],
                        'type': 'polygon'
                      }
                    }
                  }
                }
              }
            }
          }
        })
        var hits = results.hits.hits
        for (var i = 0; i < hits.length; i++) {
          var lastChar = hits[i]._id.slice(-1)
          if (!isNaN(lastChar)) {
            if (!this.getAreasServiced.includes(hits[i]._id)) {
              this.addArea(hits[i]._id)
            }
          } else {
            if (!this.getAreasServiced.includes(hits[i]._id.slice(0, -1))) {
              this.addArea(hits[i]._id.slice(0, -1))
            }
          }
        }
      } catch (error) {
        console.log(error)
      }
    },
    async save () {
      this.validate()
      if (this.formInvalid === false) {
        this.updateSelectedServices(this.selectedServices)
        this.syncData()
        this.showDismissibleAlert = true
        if (!this.complete) {
          router.replace('/profile/business/details/pictures')
        }
      }
    },
    validate () {
      this.formInvalid = false

      if (this.getSelectedServices.length === 0) {
        this.serviceCategoriesInvalid = true
        this.formInvalid = true
      } else {
        this.serviceCategoriesInvalid = false
      }

      if (!this.getCostLevel) {
        this.averageCostInvalid = true
        this.formInvalid = true
      } else {
        this.averageCostInvalid = false
      }

      if (!this.getAvailabilityLevel) {
        this.availabilityInvalid = true
        this.formInvalid = true
      } else {
        this.availabilityInvalid = false
      }

      if (!this.getEmergencies) {
        this.emergenciesInvalid = true
        this.formInvalid = true
      } else {
        this.emergenciesInvalid = false
      }

      if (this.getAreasServiced.length === 0) {
        this.serviceAreaInvalid = true
        this.formInvalid = true
      } else {
        this.serviceAreaInvalid = false
      }
    },
    ...mapMutations('business', [
      'updateSelectedServices',
      'updateCostLevel',
      'updateAvailabilityLevel',
      'updateEmergencies',
      'updateSpecialties',
      'addArea',
      'removeArea',
      'updateServiceAreaCoords',
      'clearAreas'
    ]),
    ...mapMutations('products', [
      'updateProduct'
    ]),
    addPlace () {
      var isValidZip = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(this.zip)
      if (!isValidZip || (this.getAreasServiced.indexOf(this.zip) >= 0)) {
        this.showPlaceAlert = true
        return
      }
      this.showPlaceAlert = false
      this.addArea(this.zip)
      this.zip = null
    },
    async getCategories () {
      var client = this.$store.getters.getBusinessApigClientFactory
      var params = {}
      var pathTemplate = '/businesses/categories'
      var method = 'GET'
      var additionalParams = {}
      var body = {}
      try {
        var result = await client.invokeApi(params, pathTemplate, method, additionalParams, body)
        this.availableServices = result.data.categories.sort()
        this.specialties = result.data.categories
      } catch (error) {
        console.log(error)
      }
    },
    async getServices (categories) {
      var client = this.$store.getters.getBusinessApigClientFactory
      var params = {}
      var pathTemplate = '/businesses/services'
      var method = 'POST'
      var additionalParams = {}
      var body = {
        'categories': categories
      }
      try {
        var result = await client.invokeApi(params, pathTemplate, method, additionalParams, body)
        this.specialties = {...this.specialties, ...result.data.services}
      } catch (error) {
        console.log(error)
      }
    },
    async addService () {
      if (this.selectedServices.length >= 3) {
        this.showTooManyServicesAlert = true
        return
      }
      if (this.selectedService !== null) {
        this.showTooManyServicesAlert = false
        // Add service to profile
        this.selectedServices.push(this.selectedService)

        // Retrieve and display associated specialties
        await this.getServices([this.selectedService])

        // Remove service from choice list
        var index = this.availableServices.indexOf(this.selectedService)
        if (index > -1) {
          this.availableServices.splice(index, 1)
        }

        // Remove current choice list selection from input
        this.selectedService = null
      }
    },
    removeService (service) {
      // Remove associated specialties from profile and data object
      var specialtiesToBeRemoved = this.specialties[service]
      var trimmedSpecialties = this.selectedSpecialties.filter(specialty => !specialtiesToBeRemoved.includes(specialty))
      this.selectedSpecialties = trimmedSpecialties

      // Remove associated from specialties data object
      delete this.specialties[service]

      // Remove service from profile
      var index = this.selectedServices.indexOf(service)
      if (index > -1) {
        this.selectedServices.splice(index, 1)
      }

      // Re-add removed service to choice list
      this.availableServices.push(service)
      this.availableServices.sort()
    },
    clearMap () {
      var oldPolygon = this.polygons.pop()
      if (oldPolygon) oldPolygon.setMap(null)
      this.updateServiceAreaCoords([])
      this.clearAreas()
    },
    async initMap () {
      var that = this
      var latlong = await GooglePlacesApi.getLatLong()
      var polygonCoords = this.getServiceAreaCoords.map(coords => {
        return {lat: coords[1], lng: coords[0]}
      })

      var map = new window.google.maps.Map(document.getElementById('map'), {
        mapTypeControlOptions: {
          mapTypeIds: [window.google.maps.MapTypeId.ROADMAP]
        },
        center: latlong,
        zoom: 9,
        fullscreenControl: false,
        streetViewControl: false
      })

      var drawingManager = new window.google.maps.drawing.DrawingManager({
        drawingMode: window.google.maps.drawing.OverlayType.MARKER,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: ['polygon']
        },
        polygonOptions: {
          zIndex: 1
        }
      })

      drawingManager.setMap(map)
      drawingManager.addListener('markercomplete', function (marker) {
        marker.setMap(null)
      })

      drawingManager.addListener('polygoncomplete', function (polygon) {
        var oldPolygon = that.polygons.pop()
        var polygonCoordinates = []
        if (oldPolygon) oldPolygon.setMap(null)
        that.polygons.push(polygon)
        polygon.getPath().forEach(function (value) {
          polygonCoordinates.push([value.lng(), value.lat()])
        })
        polygonCoordinates.push(polygonCoordinates[0])
        that.updateServiceAreaCoords(polygonCoordinates)
      })

      if (polygonCoords) {
        var previousCoordinates = new window.google.maps.Polygon({
          paths: polygonCoords,
          zIndex: 1
        })
        that.polygons.push(previousCoordinates)
        previousCoordinates.setMap(map)
      }
    }
  },
  mounted () {
    this.initMap()
  },
  async created () {
    this.complete = this.profileIsComplete.profileComplete
    await this.getCategories()
    this.selectedServices = this.getSelectedServices
    var that = this
    if (this.getHasAdvertisingPlan) {
      await this.getServices(this.selectedServices)
    }

    // Remove selected services from choice list
    this.selectedServices.forEach(function (service) {
      var index = that.availableServices.indexOf(service)
      if (index > -1) {
        that.availableServices.splice(index, 1)
      }
    })
  }
}
</script>

<style scoped>
.map-row {
  height: 700px;
  margin-bottom: 1rem;
}

input {
  width: 100%;
}

.jumbotron {
  background-color:  #f1f1f1  !important;
  margin-bottom: 3rem !important;
  border-radius: 25px;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.row {
  padding-bottom: 1rem;
}

.no-margin-bottom {
  margin-bottom: 0px;
}

.remove-button {
  margin-top: 0px !important;
  margin: .5rem;
  position: relative;
}

.red-text {
  color: red;
}

@media (max-width: 768px) {
  .text-right {
    text-align: left !important;
    padding-left: 1rem;
  }
}

.no-margins {
  padding-top: 0px;
  padding-bottom: 0px;
}

.fa-window-close {
  position: absolute;
  top: -6px;
  color: red;
}

@media (max-width: 768px) {
  .add-button {
    margin-top: .5rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/profile/businessProfile/details/details/Services.vue